import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Switch, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { orderAPI } from "../services/api";

interface Permission {
  name: string;
  enabled: boolean;
  key: string;
  description?: string; // Add description field
}

const ManagePermissions: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingIdx, setPendingIdx] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    setLoading(true);
    orderAPI.permissionsSetting()
      .then((res) => {
        // API returns [{ title, value, key, description }]
        const perms = (res.data || []).map((p: any) => ({
          name: p.title,
          enabled: !!p.value,
          key: p.key,
          description: p.description, // Map description
        }));
        setPermissions(perms);
      })
      .catch(() => setPermissions([]))
      .finally(() => setLoading(false));
  }, []);

  // Call API to update permission
  const updatePermission = async (idx: number, enabled: boolean) => {
    const perm = permissions[idx];
    setLoading(true);
    try {
      await orderAPI.updatePermissionSetting({ key: perm.key, value: enabled ? 1 : 0 });
      setPermissions((prev) =>
        prev.map((p, i) => (i === idx ? { ...p, enabled } : p))
      );
    } catch {
      // Optionally show error
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (idx: number) => {
    if (permissions[idx].enabled) {
      // If toggling OFF, show confirmation
      setPendingIdx(idx);
      setConfirmOpen(true);
    } else {
      // If toggling ON, update directly
      updatePermission(idx, true);
    }
  };

  const handleConfirm = () => {
    if (pendingIdx !== null) {
      updatePermission(pendingIdx, false);
      setConfirmOpen(false);
      setPendingIdx(null);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingIdx(null);
  };

  return (
    <Box sx={{ px: 3, py: 2.5, backgroundColor: "background.default", minHeight: "100vh" }}>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 3,
        p: 2,
        borderRadius: 3,
        background: "#fff",
        boxShadow: "0 4px 18px -4px rgba(0,0,0,0.12)",
        border: "1px solid",
        borderColor: "grey.200",
      }}>
        <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
          Manage Permissions
        </Typography>
      </Box>
      <Paper elevation={1} sx={{ p: 3, width:"100%", mx: "auto" }}>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : permissions.length === 0 ? (
          <Typography>No permissions found.</Typography>
        ) : (
          permissions.map((perm, idx) => (
            <React.Fragment key={perm.key}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
                <Box>
                  <Typography fontWeight={600}>{perm.name}</Typography>
                  {perm.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                      {perm.description}
                    </Typography>
                  )}
                </Box>
                <Switch
                  checked={perm.enabled}
                  onChange={() => handleToggle(idx)}
                  color="primary"
                  disabled={loading}
                />
              </Box>
              {idx < permissions.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
      </Paper>
      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Disable</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to turn off this permission?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} color="error" variant="contained">
            Yes, Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagePermissions;